import dayjs from 'dayjs';
import { CALENDAR_CONSTANTS } from './constants';

/**
 * Convert time to Y position in pixels
 * @param {dayjs.Dayjs} time - The time to convert
 * @param {dayjs.Dayjs} dayStart - Start of the day (with earliest working hour)
 * @param {number} hourHeight - Pixels per hour (affected by zoom)
 * @returns {number} Y position in pixels
 */
export const timeToPixels = (time, dayStart, hourHeight = CALENDAR_CONSTANTS.HOUR_HEIGHT) => {
    const minutesFromStart = time.diff(dayStart, 'minute');
    return (minutesFromStart / 60) * hourHeight;
};

/**
 * Convert pixel position to time
 * @param {number} pixels - Y position in pixels
 * @param {dayjs.Dayjs} dayStart - Start of the day
 * @param {number} hourHeight - Pixels per hour
 * @returns {dayjs.Dayjs} Calculated time
 */
export const pixelsToTime = (pixels, dayStart, hourHeight = CALENDAR_CONSTANTS.HOUR_HEIGHT) => {
    const minutesFromStart = Math.round((pixels / hourHeight) * 60);
    return dayStart.add(minutesFromStart, 'minute');
};

/**
 * Calculate event height in pixels based on duration
 * @param {dayjs.Dayjs} startTime
 * @param {dayjs.Dayjs} endTime
 * @param {number} hourHeight
 * @returns {number} Height in pixels
 */
export const calculateEventHeight = (startTime, endTime, hourHeight = CALENDAR_CONSTANTS.HOUR_HEIGHT) => {
    const durationMinutes = endTime.diff(startTime, 'minute');
    return Math.max((durationMinutes / 60) * hourHeight, CALENDAR_CONSTANTS.MIN_EVENT_HEIGHT);
};

/**
 * Round time to nearest grid snap interval
 * @param {dayjs.Dayjs} time
 * @param {number} snapMinutes - Snap interval in minutes
 * @returns {dayjs.Dayjs} Snapped time
 */
export const snapToGrid = (time, snapMinutes = CALENDAR_CONSTANTS.GRID_SNAP_MINUTES) => {
    const minutes = time.minute();
    const snappedMinutes = Math.round(minutes / snapMinutes) * snapMinutes;
    return time.minute(snappedMinutes).second(0).millisecond(0);
};

/**
 * Get the earliest and latest times for a day considering working hours
 * @param {dayjs.Dayjs} date
 * @param {Array} workingHours - Array of working hour objects
 * @returns {{start: dayjs.Dayjs, end: dayjs.Dayjs, isWorkingDay: boolean}}
 */
export const getDayBounds = (date, workingHours = []) => {
    // Map Polish day names to English constants used by API
    const dayNameMap = {
        'PONIEDZIAŁEK': 'MONDAY',
        'WTOREK': 'TUESDAY',
        'ŚRODA': 'WEDNESDAY',
        'CZWARTEK': 'THURSDAY',
        'PIĄTEK': 'FRIDAY',
        'SOBOTA': 'SATURDAY',
        'NIEDZIELA': 'SUNDAY'
    };

    const polishDayName = date.format('dddd').toUpperCase();
    const dayOfWeek = dayNameMap[polishDayName] || polishDayName; // Fallback to original if not Polish

    const dayHours = workingHours.find(wh => wh.dayOfWeek === dayOfWeek);

    if (dayHours) {
        const [startHour, startMinute] = dayHours.openingTime.split(':').map(Number);
        const [endHour, endMinute] = dayHours.closingTime.split(':').map(Number);

        return {
            start: date.hour(startHour).minute(startMinute).second(0),
            end: date.hour(endHour).minute(endMinute).second(0),
            isWorkingDay: true,
        };
    }

    // Return null bounds if no working hours for this day
    return {
        start: null,
        end: null,
        isWorkingDay: false,
    };
};

/**
 * Calculate the time range for the entire week view
 * @param {dayjs.Dayjs} weekStart
 * @param {Array} workingHours
 * @returns {{earliestTime: string, latestTime: string, totalHours: number}}
 */
export const getWeekTimeRange = (weekStart, workingHours = []) => {
    let earliestMinutes = 24 * 60; // Start with end of day
    let latestMinutes = 0;
    let hasAnyWorkingDay = false;

    // Check each day of the week
    for (let i = 0; i < 7; i++) {
        const day = weekStart.add(i, 'day');
        const dayBounds = getDayBounds(day, workingHours);

        if (dayBounds.isWorkingDay && dayBounds.start && dayBounds.end) {
            hasAnyWorkingDay = true;
            const startMinutes = dayBounds.start.hour() * 60 + dayBounds.start.minute();
            const endMinutes = dayBounds.end.hour() * 60 + dayBounds.end.minute();

            earliestMinutes = Math.min(earliestMinutes, startMinutes);
            latestMinutes = Math.max(latestMinutes, endMinutes);
        }
    }

    // If no working days found, return reasonable defaults
    if (!hasAnyWorkingDay) {
        return {
            earliestTime: '08:00',
            latestTime: '20:00',
            totalHours: 12,
        };
    }

    // Round to nearest hour for cleaner display
    const earliestHour = Math.floor(earliestMinutes / 60);
    const latestHour = latestMinutes % 60 === 0 ? latestMinutes / 60 : Math.ceil(latestMinutes / 60);

    return {
        earliestTime: `${earliestHour.toString().padStart(2, '0')}:00`,
        latestTime: `${latestHour.toString().padStart(2, '0')}:00`,
        totalHours: latestHour - earliestHour,
    };
};

/**
 * Check if a time is within working hours for a specific day
 * @param {dayjs.Dayjs} time
 * @param {Array} workingHours
 * @returns {boolean}
 */
export const isWithinWorkingHours = (time, workingHours = []) => {
    const dayBounds = getDayBounds(time, workingHours);

    if (!dayBounds.isWorkingDay || !dayBounds.start || !dayBounds.end) {
        return false;
    }

    return time.isSameOrAfter(dayBounds.start) && time.isSameOrBefore(dayBounds.end);
};

/**
 * Get non-working hour blocks for a specific day
 * @param {dayjs.Dayjs} date
 * @param {Array} workingHours
 * @param {Object} weekTimeRange - Result from getWeekTimeRange
 * @returns {Array} Array of non-working blocks {start, end, type}
 */
export const getNonWorkingBlocks = (date, workingHours, weekTimeRange) => {
    const blocks = [];
    const dayBounds = getDayBounds(date, workingHours);

    // Parse week range times
    const [weekStartHour] = weekTimeRange.earliestTime.split(':').map(Number);
    const [weekEndHour] = weekTimeRange.latestTime.split(':').map(Number);

    const calendarStart = date.hour(weekStartHour).minute(0).second(0);
    const calendarEnd = date.hour(weekEndHour).minute(59).second(59);

    if (!dayBounds.isWorkingDay) {
        // Entire day is non-working
        blocks.push({
            start: calendarStart,
            end: calendarEnd,
            type: 'no-work-day'
        });
    } else if (dayBounds.start && dayBounds.end) {
        // Add block before working hours
        if (dayBounds.start.isAfter(calendarStart)) {
            blocks.push({
                start: calendarStart,
                end: dayBounds.start,
                type: 'before-hours'
            });
        }

        // Add block after working hours
        if (dayBounds.end.isBefore(calendarEnd)) {
            blocks.push({
                start: dayBounds.end,
                end: calendarEnd,
                type: 'after-hours'
            });
        }
    }

    return blocks;
};

/**
 * Format duration for display
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration (e.g., "1h 30m", "45m")
 */
export const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
};

/**
 * Parse duration from backend format (e.g., "PT1H30M") to minutes
 * @param {Object} duration - Duration object with seconds/nanos
 * @returns {number} Duration in minutes
 */
export const parseDurationToMinutes = (duration) => {
    if (!duration) return 60; // Default 1 hour

    // Handle duration object with seconds/nanos
    if (duration.seconds !== undefined) {
        return Math.round(duration.seconds / 60);
    }

    // Handle ISO 8601 duration string if needed
    if (typeof duration === 'string' && duration.startsWith('PT')) {
        const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
        if (match) {
            const hours = parseInt(match[1] || 0);
            const minutes = parseInt(match[2] || 0);
            return hours * 60 + minutes;
        }
    }

    return 60; // Default fallback
};

/**
 * Check if two time ranges overlap
 * @param {Object} range1 - {start: dayjs, end: dayjs}
 * @param {Object} range2 - {start: dayjs, end: dayjs}
 * @returns {boolean}
 */
export const timeRangesOverlap = (range1, range2) => {
    return range1.start.isBefore(range2.end) && range1.end.isAfter(range2.start);
};

/**
 * Get available time slots for a given duration on a specific day
 * @param {dayjs.Dayjs} date
 * @param {number} durationMinutes
 * @param {Array} existingEvents
 * @param {Object} workingHours
 * @returns {Array} Available slots [{start: dayjs, end: dayjs}]
 */
export const getAvailableSlots = (date, durationMinutes, existingEvents = [], workingHours) => {
    const dayBounds = getDayBounds(date, workingHours);

    if (!dayBounds.isWorkingDay || !dayBounds.start || !dayBounds.end) {
        return [];
    }

    const availableSlots = [];

    // Sort events by start time
    const sortedEvents = [...existingEvents].sort((a, b) =>
        dayjs(a.startTime).diff(dayjs(b.startTime))
    );

    let currentTime = dayBounds.start;

    // Check gaps between events
    sortedEvents.forEach(event => {
        const eventStart = dayjs(event.startTime);
        const eventEnd = dayjs(event.endTime);

        // If there's a gap before this event
        if (currentTime.add(durationMinutes, 'minute').isBefore(eventStart) ||
            currentTime.add(durationMinutes, 'minute').isSame(eventStart)) {
            availableSlots.push({
                start: currentTime.clone(),
                end: currentTime.add(durationMinutes, 'minute'),
            });
        }

        // Move current time to after this event
        if (eventEnd.isAfter(currentTime)) {
            currentTime = eventEnd;
        }
    });

    // Check if there's time after the last event
    if (currentTime.add(durationMinutes, 'minute').isBefore(dayBounds.end) ||
        currentTime.add(durationMinutes, 'minute').isSame(dayBounds.end)) {
        availableSlots.push({
            start: currentTime.clone(),
            end: currentTime.add(durationMinutes, 'minute'),
        });
    }

    return availableSlots;
};